import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Loader2, Tag, Percent, DollarSign, Trash2, Edit,
  Copy, Calendar, Users, BarChart3, Search, Send,
} from 'lucide-react';

const PromoCodesPage = () => {
  const { api } = useAuth();
  const [codes, setCodes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [search, setSearch] = useState('');
  const [sendingPromo, setSendingPromo] = useState(null);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/promo-codes?limit=100');
      setCodes(res.data?.promo_codes || []);
      setTotal(res.data?.total || 0);
    } catch {
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const deleteCode = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      await api.delete(`/promo-codes/${id}`);
      toast.success('Promo code deleted');
      fetchCodes();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleActive = async (code) => {
    try {
      await api.put(`/promo-codes/${code.id}`, { is_active: !code.is_active });
      toast.success(code.is_active ? 'Deactivated' : 'Activated');
      fetchCodes();
    } catch { toast.error('Failed to update'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const filtered = search
    ? codes.filter(c => c.code.toLowerCase().includes(search.toLowerCase()) || (c.description || '').toLowerCase().includes(search.toLowerCase()))
    : codes;

  const stats = {
    total,
    active: codes.filter(c => c.is_active).length,
    totalRedemptions: codes.reduce((s, c) => s + (c.current_uses || 0), 0),
  };

  return (
    <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
      <div className="space-y-6" data-testid="promo-codes-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Promo Codes</h1>
            <p className="text-slate-500 mt-1 text-sm">Create and manage discount codes for courses and bundles</p>
          </div>
          <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={() => { setEditingCode(null); setShowCreate(true); }} data-testid="create-promo-btn">
            <Plus className="h-4 w-4 mr-2" /> Create Promo Code
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total Codes', value: stats.total, icon: Tag, color: 'text-[#20acf6]' },
            { label: 'Active', value: stats.active, icon: Percent, color: 'text-green-600' },
            { label: 'Redemptions', value: stats.totalRedemptions, icon: Users, color: 'text-purple-600' },
          ].map((s, i) => (
            <Card key={i} className="border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-slate-50 ${s.color}`}><s.icon className="h-5 w-5" /></div>
                <div>
                  <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search codes..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="promo-search" />
        </div>

        {/* Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                {search ? 'No codes match your search.' : 'No promo codes yet. Create your first one above.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Code</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Discount</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Applies To</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Uses</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Valid</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                      <th className="px-4 py-3 text-right font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(c => (
                      <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`promo-row-${c.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-[#2a3e5d] bg-slate-100 px-2 py-0.5 rounded text-xs">{c.code}</span>
                            <button onClick={() => copyCode(c.code)} className="text-slate-400 hover:text-[#20acf6]" title="Copy code">
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {c.description && <p className="text-xs text-slate-500 mt-0.5">{c.description}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-[#2a3e5d]">
                            {c.discount_type === 'percentage' ? `${c.discount_value}%` : `£${c.discount_value.toFixed(2)}`}
                          </span>
                          <span className="text-xs text-slate-400 ml-1">{c.discount_type === 'percentage' ? 'off' : 'fixed'}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs capitalize">{c.applicable_to || 'All'}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {c.current_uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ''}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {c.valid_until ? `Until ${c.valid_until.slice(0, 10)}` : 'No expiry'}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={c.is_active ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-slate-100 text-slate-500 text-[10px]'}>
                            {c.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => setSendingPromo(c)} title="Send to organisations" data-testid={`send-promo-${c.id}`}>
                              <Send className="h-4 w-4 text-[#20acf6]" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => toggleActive(c)} title={c.is_active ? 'Deactivate' : 'Activate'} data-testid={`toggle-promo-${c.id}`}>
                              <Tag className={`h-4 w-4 ${c.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => { setEditingCode(c); setShowCreate(true); }} data-testid={`edit-promo-${c.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => deleteCode(c.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-promo-${c.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <PromoCodeDialog
        open={showCreate}
        onClose={() => { setShowCreate(false); setEditingCode(null); }}
        editing={editingCode}
        onSaved={fetchCodes}
      />

      {/* Send Bulk Email Dialog */}
      {sendingPromo && (
        <SendPromoDialog
          open={!!sendingPromo}
          onClose={() => setSendingPromo(null)}
          promo={sendingPromo}
        />
      )}
    </DashboardLayout>
  );
};

const PromoCodeDialog = ({ open, onClose, editing, onSaved }) => {
  const { api } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '', description: '', discount_type: 'percentage', discount_value: 10,
    applicable_to: 'all', max_uses: '', max_uses_per_user: 1,
    valid_from: '', valid_until: '', min_purchase_amount: '', is_active: true,
  });

  useEffect(() => {
    if (editing) {
      setForm({
        code: editing.code || '',
        description: editing.description || '',
        discount_type: editing.discount_type || 'percentage',
        discount_value: editing.discount_value || 10,
        applicable_to: editing.applicable_to || 'all',
        max_uses: editing.max_uses || '',
        max_uses_per_user: editing.max_uses_per_user || 1,
        valid_from: editing.valid_from ? editing.valid_from.slice(0, 16) : '',
        valid_until: editing.valid_until ? editing.valid_until.slice(0, 16) : '',
        min_purchase_amount: editing.min_purchase_amount || '',
        is_active: editing.is_active !== false,
      });
    } else {
      setForm({
        code: '', description: '', discount_type: 'percentage', discount_value: 10,
        applicable_to: 'all', max_uses: '', max_uses_per_user: 1,
        valid_from: '', valid_until: '', min_purchase_amount: '', is_active: true,
      });
    }
  }, [editing, open]);

  const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 8; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    setForm(f => ({ ...f, code }));
  };

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    if (!form.discount_value || form.discount_value <= 0) { toast.error('Discount value must be positive'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        code: form.code.toUpperCase().trim(),
        discount_value: Number(form.discount_value),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        max_uses_per_user: Number(form.max_uses_per_user) || 1,
        min_purchase_amount: form.min_purchase_amount ? Number(form.min_purchase_amount) : null,
        valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      };

      if (editing) {
        await api.put(`/promo-codes/${editing.id}`, payload);
        toast.success('Promo code updated');
      } else {
        await api.post('/promo-codes', payload);
        toast.success('Promo code created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="promo-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#2a3e5d]">{editing ? 'Edit Promo Code' : 'Create Promo Code'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Code */}
          <div>
            <Label>Code</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. CARE20"
                className="font-mono uppercase"
                disabled={!!editing}
                data-testid="promo-code-input"
              />
              {!editing && (
                <Button variant="outline" size="sm" onClick={generateCode} className="shrink-0">
                  Generate
                </Button>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <Label>Description (optional)</Label>
            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="e.g. 20% off for new organisations" className="mt-1" data-testid="promo-desc-input" />
          </div>

          {/* Discount */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Discount Type</Label>
              <Select value={form.discount_type} onValueChange={v => setForm(f => ({ ...f, discount_type: v }))}>
                <SelectTrigger className="mt-1" data-testid="promo-discount-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed Amount (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Discount Value</Label>
              <div className="relative mt-1">
                {form.discount_type === 'percentage'
                  ? <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  : <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />}
                <Input type="number" value={form.discount_value} onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))}
                  className="pl-10" data-testid="promo-discount-value" />
              </div>
            </div>
          </div>

          {/* Applicable to */}
          <div>
            <Label>Applies To</Label>
            <Select value={form.applicable_to} onValueChange={v => setForm(f => ({ ...f, applicable_to: v }))}>
              <SelectTrigger className="mt-1" data-testid="promo-applies-to"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses & Bundles</SelectItem>
                <SelectItem value="courses">Specific Courses</SelectItem>
                <SelectItem value="bundles">Specific Bundles</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Usage limits */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Max Total Uses</Label>
              <Input type="number" value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
                placeholder="Unlimited" className="mt-1" data-testid="promo-max-uses" />
            </div>
            <div>
              <Label>Max Per User</Label>
              <Input type="number" value={form.max_uses_per_user} onChange={e => setForm(f => ({ ...f, max_uses_per_user: e.target.value }))}
                className="mt-1" data-testid="promo-max-per-user" />
            </div>
          </div>

          {/* Valid dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Valid From</Label>
              <Input type="datetime-local" value={form.valid_from} onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))}
                className="mt-1" data-testid="promo-valid-from" />
            </div>
            <div>
              <Label>Valid Until</Label>
              <Input type="datetime-local" value={form.valid_until} onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))}
                className="mt-1" data-testid="promo-valid-until" />
            </div>
          </div>

          {/* Min purchase */}
          <div>
            <Label>Minimum Purchase Amount (£)</Label>
            <Input type="number" value={form.min_purchase_amount} onChange={e => setForm(f => ({ ...f, min_purchase_amount: e.target.value }))}
              placeholder="No minimum" className="mt-1" data-testid="promo-min-purchase" />
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3 pt-2">
            <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} data-testid="promo-active-switch" />
            <Label>Active</Label>
          </div>

          {/* Save */}
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={handleSave} disabled={saving} data-testid="promo-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editing ? 'Update' : 'Create'} Promo Code
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SendPromoDialog = ({ open, onClose, promo }) => {
  const { api } = useAuth();
  const [sending, setSending] = useState(false);
  const [target, setTarget] = useState('all_orgs');
  const [subject, setSubject] = useState(`Exclusive Discount: Use code ${promo?.code || ''}`);
  const [message, setMessage] = useState(`We're offering you an exclusive discount on our care training courses.\n\nUse the code below at checkout to save on your next course or bundle purchase.`);
  const [result, setResult] = useState(null);

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await api.post(`/promo-codes/${promo.id}/send-bulk`, { target, subject, message });
      setResult(res.data);
      toast.success(`Promo code sent to ${res.data.recipient_count} recipients`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="send-promo-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#2a3e5d]">Send Promo Code: {promo.code}</DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="text-center py-6 space-y-3">
            <Send className="h-10 w-10 text-green-500 mx-auto" />
            <p className="text-lg font-semibold text-[#2a3e5d]">Sent Successfully</p>
            <p className="text-sm text-slate-600">{result.message}</p>
            <Button onClick={onClose} className="bg-[#20acf6] hover:bg-[#3bb8f7]">Close</Button>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div>
              <Label>Send To</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="mt-1" data-testid="send-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_orgs">All Organisations</SelectItem>
                  <SelectItem value="all_users">All Active Users</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email Subject</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} className="mt-1" data-testid="send-subject" />
            </div>
            <div>
              <Label>Message</Label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-[#20acf6]" rows={4} data-testid="send-message" />
              <p className="text-xs text-slate-400 mt-1">The promo code and discount details will be appended automatically.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={handleSend} disabled={sending} data-testid="send-promo-submit">
                {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Send to {target === 'all_users' ? 'All Users' : 'Organisations'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PromoCodesPage;
